// Э-8. Проблемы, предупреждения и предложения прорабов по сдвигу плана — всё, что требует решения.

import { useState } from "react";
import { fmtDate, fmtDateTime } from "../../shared/format";
import { useLive } from "../../shared/live";
import { setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Drawer, Empty, Field, Input, Loading, Select, Tabs, useAsync, useToast } from "../../shared/ui";
import { api, can, floorAbbr, floorName, live, useCtx } from "../ctx";

const ISSUE_ST: Record<string, { label: string; cls: string }> = {
  open: { label: "открыта", cls: "s-loading" },
  in_progress: { label: "в работе", cls: "s-planned" },
  closed: { label: "закрыта", cls: "s-done" },
};
const REQ_ST: Record<string, string> = { pending: "ждёт решения", approved: "одобрено", rejected: "отклонено" };

type Tab = "issues" | "alerts" | "requests";

export function IssuesPage({ id }: { id: string }) {
  const { me } = useCtx();
  const r = useRoute();
  const tab = (r.query.get("tab") as Tab) || "issues";
  const b = useAsync<any>(() => api.get(`/buildings/${id}`), [id]);
  const manager = can(me, "issues.manage");
  const decider = can(me, "plan.shift");
  const reqs = useAsync<any[]>(() => api.get(`/buildings/${id}/plan-requests`), [id]);
  const pending = (reqs.data || []).filter((x) => x.status === "pending").length;
  useLive(live, ["plan."], () => reqs.reload(), 1000);
  return (
    <>
      <TopBar title="Проблемы и решения" />
      <div className="content">
        <Tabs<Tab> value={tab} onChange={(v) => setQuery({ tab: v === "issues" ? null : v })} items={[
          { value: "issues", label: "Проблемы" },
          { value: "alerts", label: "Предупреждения" },
          { value: "requests", label: `Сдвиги плана${pending ? ` · ${pending}` : ""}` },
        ]} />
        {tab === "issues" && <IssuesTab id={id} manager={manager} projectId={b.data?.project_id} me={me.id} />}
        {tab === "alerts" && <AlertsTab id={id} />}
        {tab === "requests" && <RequestsTab data={reqs} manager={decider} />}
      </div>
    </>
  );
}

function IssuesTab({ id, manager, projectId, me }: { id: string; manager: boolean; projectId?: string; me: string }) {
  const toast = useToast();
  const [status, setStatus] = useState("active");
  const list = useAsync<any[]>(() => api.get(`/buildings/${id}/issues`), [id]);
  const cats = useAsync<any[]>(() => api.get("/issue-categories"), []);
  const people = useAsync<any[]>(() => (manager && projectId ? api.get(`/projects/${projectId}/members`) : Promise.resolve([])), [manager, projectId]);
  const [open, setOpen] = useState<any | null>(null);
  // «Создать проблему» с главного экрана корпуса открывает форму сразу (?new=1)
  const [creating, setCreating] = useState(() => new URLSearchParams(location.hash.split("?")[1] || "").get("new") === "1");
  useLive(live, ["issue."], () => list.reload(), 800);
  const rows = (list.data || []).filter((i) => status === "all" || (status === "active" ? i.status !== "closed" : i.status === status));
  const catName = (cid: string | null) => cats.data?.find((c) => c.id === cid)?.name;
  return (
    <>
      <div className="row between">
        <Select aria-label="Статус" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 200 }} options={[
          { value: "active", label: "Открытые и в работе" }, { value: "open", label: "Открытые" }, { value: "in_progress", label: "В работе" },
          { value: "closed", label: "Закрытые" }, { value: "all", label: "Все" },
        ]} />
        <Button variant="primary" onClick={() => setCreating(true)}>Новая проблема</Button>
      </div>
      {!list.data ? <Loading error={list.error} onRetry={list.reload} /> : rows.length === 0 ? <Empty>Проблем нет.</Empty> : (
        <div className="panel table-wrap">
          <table className="table">
            <thead><tr><th>Где</th><th>Что случилось</th><th>Категория</th><th>Ответственный</th><th>Срок</th><th>Статус</th></tr></thead>
            <tbody>{rows.map((i) => (
              <tr key={i.id} className="click" onClick={() => setOpen(i)}>
                <td>{i.floor_number ? floorAbbr(i.floor_number) : "—"}{i.grip_code ? `, ${i.grip_code}` : ""}</td>
                <td>{i.description}<div className="muted small">{i.created_by}, {fmtDateTime(i.created_at)}</div></td>
                <td>{catName(i.category_id) || "—"}</td>
                <td>{i.assignee || <span className="muted">не назначен</span>}</td>
                <td className={i.overdue ? "strong" : ""} style={i.overdue ? { color: "var(--st-conflict)" } : undefined}>{i.due_date ? fmtDate(i.due_date) : "—"}{i.overdue ? " · просрочено" : ""}</td>
                <td><span className={`tag ${ISSUE_ST[i.status]?.cls}`}>{ISSUE_ST[i.status]?.label}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {open && <IssueDrawer i={open} manager={manager} me={me} cats={cats.data || []} people={people.data || []} onClose={() => setOpen(null)}
        onSaved={(x) => { setOpen(x); list.reload(); }} />}
      {creating && <NewIssue id={id} cats={cats.data || []} onClose={() => setCreating(false)} onDone={() => { setCreating(false); list.reload(); toast.ok("Проблема добавлена"); }} />}
    </>
  );
}

function IssueDrawer({ i, manager, me, cats, people, onClose, onSaved }: { i: any; manager: boolean; me: string; cats: any[]; people: any[]; onClose: () => void; onSaved: (x: any) => void }) {
  const toast = useToast();
  const [comment, setComment] = useState("");
  const photos = useAsync<any[]>(() => api.get("/photos", { building: i.building_id, floor: i.floor_number, grip: i.grip_code }), [i.id]);
  const patch = async (body: any) => {
    try { onSaved(await api.patch(`/issues/${i.id}`, body)); setComment(""); } catch (e) { toast.error(e); }
  };
  const mine = i.created_by_id === me;
  return (
    <Drawer narrow title={i.floor_number ? `${floorName(i.floor_number)}${i.grip_code ? `, ${i.grip_code}` : ""}` : "Проблема"} onClose={onClose}>
      <div className="stack">
        <p style={{ fontSize: 16 }}>{i.description}</p>
        <div className="small muted">{i.created_by}, {fmtDateTime(i.created_at)}</div>
        <div className="row">
          {i.status !== "in_progress" && i.status !== "closed" && <Button onClick={() => patch({ status: "in_progress" })}>Взять в работу</Button>}
          {i.status !== "closed" && (manager || mine) && <Button variant="primary" onClick={() => patch({ status: "closed", comment: comment || undefined })}>Закрыть</Button>}
          {i.status === "closed" && <Button onClick={() => patch({ status: "open" })}>Открыть снова</Button>}
        </div>
        {manager && (
          <div className="form-grid">
            <Field label="Ответственный"><Select value={i.assignee_id || ""} placeholder="—" onChange={(e) => patch({ assignee_id: e.target.value || null })} options={people.map((p) => ({ value: p.id, label: p.full_name }))} /></Field>
            <Field label="Срок"><Input type="date" value={i.due_date || ""} onChange={(e) => patch({ due_date: e.target.value || null })} /></Field>
            <Field label="Категория" wide><Select value={i.category_id || ""} placeholder="—" onChange={(e) => patch({ category_id: e.target.value || null })} options={cats.map((c) => ({ value: c.id, label: c.name }))} /></Field>
          </div>
        )}
        {photos.data && photos.data.length > 0 && (
          <div className="stack tight"><h3>Фото с этой захватки</h3>
            <div className="photos">{photos.data.slice(0, 12).map((p) => (
              <a key={p.id} href={api.base + `/photos/${p.id}/file?token=${api.token}`} target="_blank" rel="noreferrer">
                <img src={api.base + `/photos/${p.id}/file?thumb=true&token=${api.token}`} alt={p.caption || "фото"} loading="lazy" />
              </a>
            ))}</div>
          </div>
        )}
        <div className="stack tight">
          <h3>Комментарии</h3>
          {(i.comments || []).length === 0 && <p className="muted small">Пока нет.</p>}
          {(i.comments || []).map((c: any, n: number) => <div key={n} className="small"><b>{c.user}</b> <span className="muted">{fmtDateTime(c.at)}</span><div>{c.text}</div></div>)}
          <textarea className="input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Что сделано, когда решится" />
          <Button disabled={!comment.trim()} onClick={() => patch({ comment })}>Добавить комментарий</Button>
        </div>
      </div>
    </Drawer>
  );
}

function NewIssue({ id, cats, onClose, onDone }: { id: string; cats: any[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [v, setV] = useState({ description: "", floor_number: "", grip_code: "", category_id: "" });
  return (
    <Drawer narrow title="Новая проблема" onClose={onClose} footer={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={v.description.trim().length < 2} onClick={async () => {
        try {
          await api.post(`/buildings/${id}/issues`, { description: v.description.trim(), floor_number: v.floor_number ? Number(v.floor_number) : null, grip_code: v.grip_code || null, category_id: v.category_id || null });
          onDone();
        } catch (e) { toast.error(e); }
      }}>Добавить</Button>
    </>}>
      <div className="form-grid">
        <Field label="Что случилось" wide><textarea className="input" rows={3} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} /></Field>
        <Field label="Этаж"><Input inputMode="numeric" value={v.floor_number} onChange={(e) => setV({ ...v, floor_number: e.target.value })} /></Field>
        <Field label="Захватка"><Input value={v.grip_code} placeholder="С1, П2…" onChange={(e) => setV({ ...v, grip_code: e.target.value.toUpperCase().replace("C", "С").replace("P", "П") })} /></Field>
        <Field label="Категория" wide><Select value={v.category_id} placeholder="—" onChange={(e) => setV({ ...v, category_id: e.target.value })} options={cats.map((c) => ({ value: c.id, label: c.name }))} /></Field>
      </div>
    </Drawer>
  );
}

function AlertsTab({ id }: { id: string }) {
  const [active, setActive] = useState(true);
  const a = useAsync<any[]>(() => api.get("/alerts", { building: id, active }), [id, active]);
  useLive(live, ["alert.", "report."], () => a.reload(), 1500);
  return (
    <>
      <label className="check"><input type="checkbox" checked={!active} onChange={(e) => setActive(!e.target.checked)} /> Показать снятые</label>
      {!a.data ? <Loading error={a.error} onRetry={a.reload} /> : a.data.length === 0 ? <Empty>Предупреждений нет — всё идёт по плану.</Empty> : (
        <div className="stack tight">{a.data.map((x) => (
          <div key={x.id} className={`alert${x.level === "risk" || x.level === "critical" ? " risk" : x.level === "info" ? " info" : ""}`} style={x.cleared_at ? { opacity: 0.6 } : undefined}>
            <div className="t">{x.text}</div>
            <div className="d small muted">{fmtDateTime(x.raised_at)}{x.cleared_at ? ` · снято ${fmtDateTime(x.cleared_at)}` : ""}</div>
          </div>
        ))}</div>
      )}
      <p className="muted small">Предупреждения система ставит и снимает сама: по отчётам, плану и порогам в настройках проекта.</p>
    </>
  );
}

function RequestsTab({ data, manager }: { data: ReturnType<typeof useAsync<any[]>>; manager: boolean }) {
  const toast = useToast();
  const [comments, setComments] = useState<Record<string, string>>({});
  const decide = async (rid: string, approve: boolean) => {
    try {
      await api.post(`/plan-requests/${rid}/decision`, { approve, comment: comments[rid] || null });
      toast.ok(approve ? "Сдвиг одобрен, план пересчитан" : "Предложение отклонено");
      data.reload();
    } catch (e) { toast.error(e); }
  };
  if (!data.data) return <Loading error={data.error} onRetry={data.reload} />;
  if (data.data.length === 0) return <Empty>Предложений нет. Прораб может предложить сдвиг на экране «График цикла».</Empty>;
  return (
    <div className="stack tight">{data.data.map((x) => (
      <div key={x.id} className="panel pad stack tight">
        <div className="row between">
          <b>{floorName(x.floor)}: сдвиг на {x.shift_days > 0 ? "+" : ""}{x.shift_days} раб. дн. {x.scope === "this" ? "(только этот этаж)" : "(и все следующие)"}</b>
          <span className={`tag ${x.status === "approved" ? "s-done" : x.status === "rejected" ? "plain" : "s-loading"}`}>{REQ_ST[x.status] || x.status}</span>
        </div>
        <div>{x.reason}</div>
        <div className="small muted">{x.author}, {fmtDateTime(x.created_at)}{x.decided_by ? ` · решение: ${x.decided_by}${x.decision_comment ? ` — ${x.decision_comment}` : ""}` : ""}</div>
        {manager && x.status === "pending" && (
          <div className="row">
            <Input className="grow" placeholder="Комментарий к решению" value={comments[x.id] || ""} onChange={(e) => setComments({ ...comments, [x.id]: e.target.value })} />
            <Button variant="primary" onClick={() => decide(x.id, true)}>Одобрить</Button>
            <Button onClick={() => decide(x.id, false)}>Отклонить</Button>
          </div>
        )}
      </div>
    ))}</div>
  );
}
